import { pick } from 'lodash';

/**
 * WordPress dependencies
 */

import {
	useState,
	useEffect,
	useContext,
} from '@wordpress/element';
import {
	useSelect,
} from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';

import {
	__,
	_x,
} from '@wordpress/i18n';

const {
	ColorPalette,
	MediaPlaceholder,
} = wp.blockEditor;

import {
	Toolbar,
	ToolbarGroup,
	ToolbarButton,
	SelectControl,
	RangeControl,
	ToggleControl,
	PanelBody,
	BaseControl,
} from '@wordpress/components';

import {
	paragraph,
	formatBold,
	formatItalic,
	link
} from '@wordpress/icons';

/**
 * Internal dependencies
 */
import icons from './icons';
import EedeeGradientPicker from '../gradient-picker/gradient-picker';
import { MediaPicker } from '../media-picker/media-picker';
import { AutoSlidesContext } from '../../../blocks/context';
import UnitButton from '../../unit-button';

import './styles/editor.scss';

							{/* onSelectURL={ url => {
								updateBackground( {
									backgroundType: 'video',
									backgroundVideo: {
										id: null,
										url: url,
										alt: null,
										type: 'video',
										embed: true,
									},
								} );
							} } */}

export default function BackgroundPanel( props ) {
	const {
		value,
		controlsEnabled = [ 'none', 'color', 'image' ],
		overlayEnabled = false,
		minWidth,
		onChangeMinWidth,
	} = props;

	const {
		backgroundColor,
		backgroundImage,
		backgroundOverlayImage,
		backgroundOverlayVideo,
		backgroundGradient,
		backgroundOverlayOpacity,
		backgroundImageSize,
		backgroundVideoSize,
		backgroundType,
		backgroundFocalPoint,
		backgroundVideoFocalPoint,
		backgroundVideo,
		backgroundVideoLoop,
		backgroundVideoMuted,
	} = value;

	const [ background, setBackground ] = useState( value ? value : {} );

	const autoSlides = useContext( AutoSlidesContext );

	useEffect( () => {
		props.onChange( background );
	}, [ background ]);

	function updateBackground( newBackground ) {

		const updated = {
			...background,
			...newBackground,
		};

		setBackground( updated );
	}

	const {
		preview,
		fetching,
		themeSupportsResponsive,
		cannotEmbed,
	} = useSelect(
		( select ) => {
			const {
				getEmbedPreview,
				isPreviewEmbedFallback,
				isRequestingEmbedPreview,
				getThemeSupports,
			} = select( coreStore );
			if ( ! backgroundVideo.url ) {
				return { fetching: false, cannotEmbed: false };
			}

			const youtube_parser = ( url ) => {
				var regExp = /^.*(?:(?:youtu\.be\/|v\/|vi\/|u\/\w\/|embed\/)|(?:(?:watch)?\?v(?:i)?=|\&v(?:i)?=))([^#\&\?]*).*/;
				var match = url.match(regExp);
				return match[1];
			}

			const embedPreview = getEmbedPreview( backgroundVideo.url );
			const previewIsFallback = isPreviewEmbedFallback( backgroundVideo.url );

			// The external oEmbed provider does not exist. We got no type info and no html.
			const badEmbedProvider =
				embedPreview?.html === false &&
				embedPreview?.type === undefined;
			// Some WordPress URLs that can't be embedded will cause the API to return
			// a valid JSON response with no HTML and `data.status` set to 404, rather
			// than generating a fallback response as other embeds do.
			const wordpressCantEmbed = embedPreview?.data?.status === 404;
			const validPreview =
				!! embedPreview && ! badEmbedProvider && ! wordpressCantEmbed;
			if ( validPreview ) {
				const ytid = youtube_parser( backgroundVideo.url );
				embedPreview.ytid = ytid;
				embedPreview.hdThumb = `https://img.youtube.com/vi/${ ytid }/maxresdefault.jpg`;
			}
			return {
				preview: validPreview ? embedPreview : undefined,
				fetching: isRequestingEmbedPreview( backgroundVideo.url ),
				themeSupportsResponsive: getThemeSupports()[
					'responsive-embeds'
				],
				cannotEmbed: ! validPreview || previewIsFallback,
			};
		},
		[ backgroundVideo.embed ]
	);

	useEffect( () => {
		updateBackground( { backgroundVideo: {
			...backgroundVideo,
			embedData: preview,
		} } );
	}, [ preview ]);

	const backgroundSizeOptions = [
		{ value: 'cover', label: _x( 'Cover', 'block layout', 'eedee' ) },
		{ value: 'contain', label: _x( 'Contain', 'block layout', 'eedee' ) },
		{ value: 'fill', label: _x( 'Fill', 'block layout', 'eedee' ) },
		{ value: 'none', label: _x( 'None', 'block layout', 'eedee' ) },
	];

	const backgroundOverlayOptions = [
		{ value: '', label: _x( 'No Overlay', 'block layout', 'eedee' ) },
		{ value: 'color', label: _x( 'Overlay Selected Color', 'block layout', 'eedee' ) },
		{ value: 'gradient', label: _x( 'Overlay Selected Gradient', 'block layout', 'eedee' ) },
	];

	const backgroundSizeDefault = 'cover';

	const bgTypeButtonGroupButtons = {
		none: {
			icon: 'no-alt',
			title: __( 'None' ),
			// className: { 'is-active': backgroundType === 'none' },
			onClick: () => {
				updateBackground( { backgroundType: 'none' } );
			},
			isActive: backgroundType === 'none',
			// isPrimary: backgroundType === 'none',
			// isSecondary: backgroundType !== 'none',
		},
		color: {
			icon: 'admin-appearance',
			title: __( 'Color' ),
			// className: { 'is-active': backgroundType === 'color' },
			onClick: () => {
				updateBackground( { backgroundType: 'color' } );
			},
			isActive: backgroundType === 'color' ||
				( 'image' === backgroundType && backgroundOverlayImage === 'color' ) ||
				( 'video' === backgroundType && backgroundOverlayVideo === 'color' ),
			// isPrimary: backgroundType === 'color' ||
			// 	( 'image' === backgroundType && backgroundOverlayImage === 'color' ) ||
			// 	( 'video' === backgroundType && backgroundOverlayVideo === 'color' ),
			// isSecondary: ! ( backgroundType === 'color' ||
			// 	( 'image' === backgroundType && backgroundOverlayImage === 'color' ) ||
			// 	( 'video' === backgroundType && backgroundOverlayVideo === 'color' ) ),
		},
		gradient: {
			icon: icons.gradient,
			title: __( 'Gradient' ),
			// className: { 'is-active': backgroundType === 'gradient' },
			onClick: () => {
				updateBackground( { backgroundType: 'gradient' } );
			},
			isActive: backgroundType === 'gradient' ||
				( 'image' === backgroundType && backgroundOverlayImage === 'gradient' ) ||
				( 'video' === backgroundType && backgroundOverlayVideo === 'gradient' ),
			// isPrimary: backgroundType === 'gradient' ||
			// 	( 'image' === backgroundType && backgroundOverlayImage === 'gradient' ) ||
			// 	( 'video' === backgroundType && backgroundOverlayVideo === 'gradient' ),
			// isSecondary: backgroundType === 'gradient' ||
			// 	( 'image' === backgroundType && backgroundOverlayImage === 'gradient' ) ||
			// 	( 'video' === backgroundType && backgroundOverlayVideo === 'gradient' ),
		},
		image: {
			icon: 'format-image',
			title: __( 'Image' ),
			// className: { 'is-active': backgroundType === 'image' },
			onClick: () => {
				updateBackground( { backgroundType: 'image' } );
			},
			isActive: backgroundType === 'image' ||
				( backgroundOverlayImage === 'color' && backgroundType === 'color' ) ||
				( backgroundOverlayImage === 'gradient' && backgroundType === 'gradient' ),
			// isPrimary: backgroundType === 'image',
			// isSecondary: backgroundType !== 'image',
		},
		video: {
			icon: 'format-video',
			title: __( 'Video' ),
			// className: { 'is-active': backgroundType === 'video' },
			onClick: () => {
				updateBackground( { backgroundType: 'video' } );
			},
			isActive: backgroundType === 'video' ||
				( backgroundOverlayVideo === 'color' && backgroundType === 'color' ) ||
				( backgroundOverlayVideo === 'gradient' && backgroundType === 'gradient' ),
			// isPrimary: backgroundType === 'video',
			// isSecondary: backgroundType !== 'video',
		},
	};

	return (
		<>
			<PanelBody
				title={ __( 'Background Settings', 'gutenslider' ) }
				initialOpen={ props.initialOpen }
				className="components-panel__body--eedee-background-panel"
			>
				<span>{ __( 'Background Type', 'gutenslider' ) }</span>
        <Toolbar
					label={ __( 'Background Options', 'gutenslider' ) }
					className={ 'eedee-bg-toolbar' }
				>
					{ Object.keys( pick( bgTypeButtonGroupButtons, controlsEnabled ) ).map( ( key ) => {
						return <ToolbarButton
							key={ bgTypeButtonGroupButtons[ key ].title }
							{ ...bgTypeButtonGroupButtons[ key ] }
						/>;
					} ) }
        </Toolbar>

				{ backgroundType === 'color' && (
					<>
						{ backgroundOverlayImage === 'color' &&
							<p className="eedee-color-info">{ __( 'The color is shown, because you selected that the background image should have an overlay. To disable it or to change the opacity, go to the image settings and change the "Overlay" option.' ) }</p>
						}
						{ backgroundOverlayVideo === 'color' &&
							<p className="eedee-color-info">{ __( 'The color is shown, because you selected that the background video should have an overlay. To disable it or to change the opacity, go to the video settings and change the "Overlay" option.' ) }</p>
						}
						<ColorPalette
							value={ backgroundColor }
							onChange={ ( col ) => updateBackground( { backgroundColor: col } ) }
						/>
					</>
				) }

				{ backgroundType === 'gradient' && (
					<>
						{ backgroundOverlayImage === 'gradient' &&
							<p className="eedee-gradient-info">{ __( 'The gradient is shown, because you selected that the background image should have an overlay. To disable it or to change the opacity, go to the image settings and change the "Overlay" option.' ) }</p>
						}
						{ backgroundOverlayVideo === 'gradient' &&
							<p className="eedee-gradient-info">{ __( 'The gradient is shown, because you selected that the background video should have an overlay. To disable it or to change the opacity, go to the video settings and change the "Overlay" option.' ) }</p>
						}
						<EedeeGradientPicker
							label={ __( 'Gradient Options' ) }
							value={ backgroundGradient }
							onChange={ ( gradient ) => {
								updateBackground( {
									backgroundGradient: gradient,
								} );
							} }
						/>
					</>
				) }

				{ backgroundType === 'image' && (
					<>
						<MediaPicker
							media={ backgroundImage }
							type={ 'image' }
							allowedTypes={ [ 'image' ] }
							labels={ {
								title: __( 'Select Background Image' ),
							} }
							onSelect={ medium => updateBackground( {
								backgroundImage: medium,
								mediaId: medium.id,
							} ) }
							onSelectURL={ url => {
								updateBackground( {
									backgroundType: 'image',
									backgroundImage: {
										id: null,
										url: url,
										alt: null,
										type: 'image',
									},
								} );
							} }
							onRemoveMedia={ () => {
								updateBackground( {
									backgroundImage: {
										id: null,
										url: null,
										alt: null,
									},
									mediaId: null,
								} );
							} }
							focalPoint={ backgroundFocalPoint }
							onSetFocalPoint={ val => {
								updateBackground( {
									backgroundFocalPoint: val,
								} );
							} }
						/>
						{ overlayEnabled && <SelectControl
							label={ __( 'Overlay', 'eedee' ) }
							className="components-background-display-select--eedee"
							value={ backgroundOverlayImage }
							options={ backgroundOverlayOptions }
							onChange={ ( val ) => updateBackground( {
								backgroundOverlayImage: val,
								backgroundOverlayVideo: '',
							} ) }
						/> }
						{ overlayEnabled && backgroundOverlayImage !== '' && <RangeControl
							label={ __( 'Overlay Opacity', 'gutenslider' ) }
							min={ 0 }
							max={ 100 }
							step={ 10 }
							value={ backgroundOverlayOpacity }
							onChange={ ( overlayOp ) => updateBackground( {
								backgroundOverlayOpacity: overlayOp,
							} ) }
						/> }
						{ ! autoSlides && <SelectControl
							label={ __( 'Display', 'eedee' ) }
							value={ backgroundImageSize ? backgroundImageSize : backgroundSizeDefault }
							options={ backgroundSizeOptions }
							onChange={ ( bgSize ) => updateBackground( { backgroundImageSize: bgSize } ) }
						/> }
					</>
				) }

				{ backgroundType === 'video' &&
					<>
						<p className="components-base-control__help">{ __( 'If you want to set a image poster for the video / that will show while the video loads or in thumbnails, just upload the image in the Image tab left and then select video again.', 'eedee' ) }</p>
						<MediaPicker
							media={ backgroundVideo }
							type={ 'video' }
							allowedTypes={ [ 'video' ] }
							labels={ {
								title: __( 'Select Background Video' ),
							} }
							onSelect={ vid => updateBackground( {
								backgroundVideo: {
									...vid,
									embed: false,
								},
								mediaId: vid.id,
							} ) }
							onRemoveMedia={ () => {
								updateBackground( {
									backgroundVideo: {
										id: null,
										url: null,
										alt: null,
									},
									mediaId: null,
								} );
							} }
							focalPoint={ backgroundVideoFocalPoint }
							onSetFocalPoint={ val => {
								updateBackground( {
									backgroundVideoFocalPoint: val,
								} );
							} }
						/>
						<ToggleControl
							label={ __( 'Mute Video', 'eedee' ) }
							help={ backgroundVideoMuted ? __( 'Muting the background video.', 'eedee' ) : __( 'Toggle to mute the video.', 'eedee' ) }
							checked={ !! backgroundVideoMuted }
							onChange={ () => updateBackground( { backgroundVideoMuted: ! backgroundVideoMuted } ) }
						/>
						<ToggleControl
							label={ __( 'Loop Video', 'eedee' ) }
							help={ backgroundVideoLoop ? __( 'Looping the background video.', 'eedee' ) : __( 'Toggle to loop the video.', 'eedee' ) }
							checked={ !! backgroundVideoLoop }
							onChange={ () => updateBackground( { backgroundVideoLoop: ! backgroundVideoLoop } ) }
						/>
						{ overlayEnabled && <SelectControl
							label={ __( 'Overlay', 'eedee' ) }
							className="components-background-display-select--eedee"
							value={ backgroundOverlayVideo }
							options={ backgroundOverlayOptions }
							onChange={ ( val ) => updateBackground( {
								backgroundOverlayVideo: val,
								backgroundOverlayImage: '',
							} ) }
						/> }
						{ overlayEnabled && backgroundOverlayVideo !== '' && <RangeControl
							label={ __( 'Overlay Opacity', 'gutenslider' ) }
							min={ 0 }
							max={ 100 }
							step={ 10 }
							value={ backgroundOverlayOpacity }
							onChange={ ( overlayOp ) => updateBackground( {
								backgroundOverlayOpacity: overlayOp,
							} ) }
						/> }
						<SelectControl
							label={ __( 'Display', 'eedee' ) }
							value={ backgroundVideoSize ? backgroundVideoSize : backgroundSizeDefault }
							options={ backgroundSizeOptions }
							onChange={ ( bgSize ) => updateBackground( { backgroundVideoSize: bgSize } ) }
						/>
					</>
				}
				{ [ 'none', 'gradient', 'color' ].indexOf( background.backgroundType ) !== -1 && autoSlides && <BaseControl>
			    <BaseControl.VisualLabel>{ __( 'Minimum Slide Width', 'gutenslider' ) }</BaseControl.VisualLabel>
							{ [ 'none', 'color', 'gradient' ].indexOf( background.backgroundType ) !== -1 && <UnitButton
									units={ [
											{ value: '%', label: '%', default: 0, min: 20, max: 100 },
											{ value: 'px', label: 'px', default: 10, min: 100, max: 1500 },
									] }
									step={ 1 }
									transformUnits={ true }
									onChange={ onChangeMinWidth }
									value={ minWidth }
							/> }
					</BaseControl> }
			</PanelBody>
		</>
	);
}
